import { FieldArray, Form, Formik } from 'formik';
import {
  Button,
  Input,
  LoadingSpinner,
  Modal,
} from '@rabbit/elements/shared-components';
import * as Yup from 'yup';
import { useContext, useState } from 'react';
import { CaseflowContext } from 'apps/sage/src/context/CaseflowContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import {
  CLAIM_CAUSE_OPTIONS,
  CLAIM_OUTCOME_OPTIONS,
  COVERAGE_OPTIONS,
  COVERAGE_OPTIONS_FATBIKES,
  FileStorageContext,
  getCompletedUploadsOfCategory,
  useManageFaults,
} from '@rabbit/bizproc/react';
import { SageFileUploader } from '../../upload-wrapper/SageFileUploader';
import {
  CompletedUploadShape,
  DocTypeShapeTypes,
  UploadedFileCategories,
} from '@rabbit/elements/shared-types';
import {
  AssessmentPart,
  CaseFactsShape,
  CaseflowCaseTypes,
  ClaimAssessmentStatus,
} from '@rabbit/bizproc/core';
import {
  FBD_Vendable,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  UserUploadedDocument,
} from '@rabbit/data/types';
import { TrashIcon } from '@heroicons/react/24/solid';
import { VeilKeys } from '@rabbit/mixmaster/veils';
import { PartStatus } from '@rabbit/sage/utils/consts.ts';
import { useGetMySagePersonas } from '@rabbit/data/portal';

interface FormValuesShape {
  assessment_parts: AssessmentPart[];
  holding_faults: string[];
  preliminary_assessment: string;
  final_assessment: string;
  claim_outcome: string;
  assessment_comment: string;
  assessment_attachment: UserUploadedDocument[];
  claim_cause: string;
  assessment_status?: ClaimAssessmentStatus | string;
  assessment_rejection_reason?: string;
}

const validationSchema = Yup.object().shape({
  assessment_parts: Yup.array(
    Yup.object({
      name: Yup.string(),
      manufacturer: Yup.string(),
      description: Yup.string(),
    })
  ),
  holding_faults: Yup.array(Yup.string()),
  preliminary_assessment: Yup.string(),
  final_assessment: Yup.string(),
  claim_outcome: Yup.string(),
  assessment_comment: Yup.string(),
  claim_cause: Yup.string(),
  assessment_attachment: Yup.array(Yup.object()),
  assessment_status: Yup.string(),
  assessment_rejection_reason: Yup.string(),
});

export default function UpdateClaimAssessmentModal({
  onClose,
}: {
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { faultList } = useManageFaults();
  const [comment, setComment] = useState(false);
  const faultOptions = !faultList?.data
    ? []
    : faultList.data
        .sort((a, b) =>
          (a.title || '').localeCompare(b.title || '', undefined, {
            sensitivity: 'base',
          })
        )
        .map((fault) => ({
          id: fault.docid,
          value: fault.docid,
          label: fault.title,
          name: fault.title,
        }));
  const { config } = useContext(AppContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { uploadQueueState, moveCompletedUploadsToAttached } =
    useContext(FileStorageContext) || {};

  const { installerPersona, repairerPersona } = useGetMySagePersonas();

  const {
    caseFacts,
    installerCaseFacts,
    repairerCaseFacts,
    warrantorCasefacts,
    alterCaseFacts,
    executeAction,
    moveSpotlight,
    operatingPersonaSingleKey,
    caseId,
    caseFlowCase,
    alterCasePublicEmail,
    caseActors,
  } = useContext(CaseflowContext) || {};

  const isRepairer =
    operatingPersonaSingleKey === PersonaTypeSingleLetter.Repairer;
  const isInstaller =
    operatingPersonaSingleKey === PersonaTypeSingleLetter.Installer;
  const isWarrantor =
    operatingPersonaSingleKey === PersonaTypeSingleLetter.Warrantor;

  // Determine which case facts to use based on persona existence
  const currentCaseFacts = isInstaller
    ? installerCaseFacts
    : isRepairer
    ? repairerCaseFacts
    : warrantorCasefacts;

  if (
    !executeAction ||
    !alterCaseFacts ||
    !moveSpotlight ||
    !operatingPersonaSingleKey ||
    !currentCaseFacts ||
    !caseId ||
    !caseFlowCase ||
    !alterCasePublicEmail ||
    !caseActors
  )
    return <LoadingSpinner size={'xs'} />;

  const initialValues: FormValuesShape = {
    assessment_parts: currentCaseFacts?.assessment_parts || [],
    holding_faults: currentCaseFacts?.holding_faults || [],
    preliminary_assessment: currentCaseFacts?.preliminary_assessment || '',
    final_assessment: currentCaseFacts?.final_assessment || '',
    claim_outcome: currentCaseFacts?.claim_outcome || '',
    claim_cause: currentCaseFacts?.claim_cause || '',
    assessment_comment: currentCaseFacts?.assessment_comment || '',
    assessment_attachment: currentCaseFacts?.assessment_attachment || [],
    assessment_status: currentCaseFacts?.assessment_status || '',
    assessment_rejection_reason:
      currentCaseFacts?.assessment_rejection_reason || '',
  };

  const onSubmit = async (values: FormValuesShape) => {
    const {
      holding_faults,
      claim_outcome,
      claim_cause,
      assessment_comment,
      preliminary_assessment,
      final_assessment,
      assessment_parts,
      assessment_attachment,
      assessment_status,
      assessment_rejection_reason,
    } = values;

    const { completed } = uploadQueueState ?? {};
    const attachment: CompletedUploadShape[] = getCompletedUploadsOfCategory(
      completed ?? [],
      UploadedFileCategories.AssessmentAttachment
    );

    try {
      setIsSubmitting(true);

      const factsToAlter = {
        assessment_comment,
        holding_faults,
        preliminary_assessment,
        final_assessment,
        claim_outcome,
        claim_cause,
        assessment_attachment:
          values.assessment_attachment &&
          values.assessment_attachment.length > 0
            ? values.assessment_attachment
            : currentCaseFacts?.assessment_attachment,
        assessment_parts,
        assessment_rejection_reason,
      } as Partial<CaseFactsShape>;
      if (assessment_status) {
        factsToAlter.assessment_status = assessment_status;
      }
      if ((isInstaller || isRepairer) && !isWarrantor) {
        factsToAlter.assessment_status = ClaimAssessmentStatus.PENDING;
      }
      await alterCaseFacts(factsToAlter);
      attachment.length > 0 && moveCompletedUploadsToAttached?.(attachment);
      toast.success(t('general.claimUpdatedSuccessfully'));
      onClose();
    } catch (err) {
      setIsSubmitting(false);
      toast.error(t('message.failedToUpdateClaimDetails'));
      console.log(err);
    }
  };

  return (
    <Modal
      settings={{
        title: t('general.updateAssessment'),
        headerBackground: true,
        handleClose: () => onClose(),
      }}
      kind="generic"
      isLoading={isSubmitting}
      className={`${
        config.CLAIMS.CLAIMS_FLOW.PARTS_ASSESSMENT
          ? 'max-w-[1024px]'
          : 'max-w-[768px]'
      }`}
    >
      <div className={'font-nunito mt-5 ml-4'}>Defective part(s)</div>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ errors, values, setFieldValue }) => (
          <Form className="mt-5 flex flex-col gap-3 px-4">
            <FieldArray name="assessment_parts">
              {({ push, remove }) => (
                <div className="flex flex-col gap-4">
                  {values.assessment_parts?.map((part, index) => (
                    <>
                      <div
                        key={index}
                        className={`grid grid-cols-[2fr_1fr_1fr_2fr_30px] items-end gap-4`}
                      >
                        {config.VENDABLES.PART_SEARCH.ENABLED && (
                          <Input
                            type="autocomplete-vendable-part"
                            name={`assessment_parts.${index}.vendableId`}
                            label={`${t('general.part')} ${index + 1}`}
                            settings={{
                              placeholder: t('general.part'),
                              tenantLink: t(VeilKeys.TENANT_LINK),
                            }}
                            onChange={async (e) => {
                              if (!e?.id) {
                                return;
                              }
                              const vendableId = e.id;
                              const vendable = await FBD_Vendable.get(
                                vendableId
                              );
                              if (vendable) {
                                void setFieldValue(
                                  `assessment_parts.${index}.name`,
                                  vendable.title
                                );
                                void setFieldValue(
                                  `assessment_parts.${index}.manufacturer`,
                                  vendable.brand
                                );
                                void setFieldValue(
                                  `assessment_parts.${index}.id`,
                                  vendable.mpn
                                );
                              }
                            }}
                          />
                        )}
                        <Input
                          type="text"
                          label={
                            config.VENDABLES.PART_SEARCH.ENABLED
                              ? ''
                              : t('general.parts')
                          }
                          name={`assessment_parts.${index}.name`}
                          settings={{
                            placeholder: t('general.parts'),
                          }}
                          className={
                            config.VENDABLES.PART_SEARCH.ENABLED
                              ? 'hidden'
                              : undefined
                          }
                        />

                        <Input
                          type="text"
                          label={t('general.partId')}
                          name={`assessment_parts.${index}.id`}
                          settings={{
                            placeholder: t('general.partId'),
                            disabled: config.VENDABLES.PART_SEARCH.ENABLED,
                          }}
                        />
                        <Input
                          type="text"
                          label={t('general.manufacturer')}
                          name={`assessment_parts.${index}.manufacturer`}
                          settings={{
                            placeholder: t('general.partManufacturer'),
                            disabled: config.VENDABLES.PART_SEARCH.ENABLED,
                          }}
                        />

                        <Input
                          type="text"
                          label={t('general.description')}
                          name={`assessment_parts.${index}.description`}
                          settings={{
                            placeholder: t('general.partDescription'),
                          }}
                        />

                        <div className="grid h-[44px] grid-cols-2 items-center gap-2 pl-2">
                          <TrashIcon
                            className="h-8 w-8 cursor-pointer text-red-500"
                            onClick={() => remove(index)}
                          />
                        </div>
                      </div>
                      <div
                        key={index}
                        className={`grid grid-cols-[2fr_2fr_2fr__30px] items-end gap-4`}
                      >
                        {config.CLAIMS.CLAIMS_FLOW.PARTS_ASSESSMENT && (
                          <>
                            <Input
                              type="select"
                              label={t('general.fault')}
                              name={`assessment_parts.${index}.faults`}
                              settings={{
                                options: faultOptions,
                                id: 'fault',
                                placeholder: t('general.pleaseSelectAFault'),
                                isMulti: false,
                              }}
                            />
                            <Input
                              type="select"
                              label={t('general.status')}
                              name={`assessment_parts.${index}.status`}
                              settings={{
                                options:
                                  PartStatus[
                                    t('tenantLink') as keyof typeof PartStatus
                                  ] ?? [],
                                id: 'status',
                                placeholder: t('general.pleaseSelectAStatus'),
                                isMulti: false,
                              }}
                            />
                            <Input
                              type="text"
                              label={t('general.tag_id')}
                              name={`assessment_parts.${index}.tag_id`}
                              settings={{
                                placeholder: t('general.tag_id'),
                              }}
                            />
                          </>
                        )}
                      </div>
                      {index < values.assessment_parts.length - 1 && (
                        <div className="border-b border-green-700"></div>
                      )}
                    </>
                  ))}
                  <Button
                    kind="outline"
                    type="button"
                    onClick={() =>
                      push({
                        name: '',
                        manufacturer: '',
                        description: '',
                        id: '',
                      })
                    }
                  >
                    {t('Add a part')}
                  </Button>
                </div>
              )}
            </FieldArray>
            <Input
              type="select"
              label={t('general.faults')}
              name="holding_faults"
              settings={{
                options: faultOptions,
                id: 'fault',
                placeholder: t('general.pleaseSelectAFault'),
                isMulti: true,
              }}
            />
            {(isInstaller || isRepairer) && (
              <Input
                type="select"
                label={t('general.assessment')}
                name="preliminary_assessment"
                settings={{
                  options:
                    config.CLAIMS.CASEFLOW_TYPE === CaseflowCaseTypes.FATBIKES
                      ? COVERAGE_OPTIONS_FATBIKES
                      : COVERAGE_OPTIONS,
                  placeholder: t('general.pleaseSelectAnAssessment'),
                }}
              />
            )}
            {!isInstaller && !isRepairer && (
              <>
                <Input
                  type="select"
                  label={t('general.finalAssessment')}
                  name="final_assessment"
                  settings={{
                    options:
                      config.CLAIMS.CASEFLOW_TYPE === CaseflowCaseTypes.FATBIKES
                        ? COVERAGE_OPTIONS_FATBIKES
                        : COVERAGE_OPTIONS,
                    placeholder: t('general.pleaseSelectAnAssessment'),
                  }}
                />
                <Input
                  type="select"
                  label={t('general.outcome')}
                  name="claim_outcome"
                  settings={{
                    options: CLAIM_OUTCOME_OPTIONS,
                    id: 'outcome',
                    placeholder: t('general.pleaseSelectAnOutcome'),
                  }}
                />
                <Input
                  type="select"
                  label={t('general.cause')}
                  name="claim_cause"
                  settings={{
                    options: CLAIM_CAUSE_OPTIONS,
                    placeholder: t('general.pleaseSelectACause'),
                  }}
                />
              </>
            )}
            {!comment && !values.assessment_comment && (
              <div className="mt-4">
                <Button
                  kind="outline"
                  type="button"
                  className="w-full"
                  onClick={() => setComment(true)}
                >
                  {t('general.additionalComment')}
                </Button>
              </div>
            )}
            {(comment || values.assessment_comment) && (
              <div>
                <Input
                  type="rich-text"
                  label={t('general.additionalComment')}
                  name="assessment_comment"
                  settings={{
                    id: 'assessment_comment',
                    placeholder: '',
                    allowSpecialCharacter: true,
                  }}
                />
              </div>
            )}
            <SageFileUploader
              label={t('general.additionalDocument')}
              identifiers={{
                category: UploadedFileCategories.AssessmentAttachment,
                docType: {
                  docid: caseId,
                  type: DocTypeShapeTypes.Case,
                },
                personaId: caseActors?.consumer ?? '',
              }}
              onUploadCompleted={(files) => {
                setFieldValue(
                  'assessment_attachment',
                  files.flatMap((file: CompletedUploadShape) =>
                    file.category ===
                      UploadedFileCategories.AssessmentAttachment &&
                    file?.uploadedFile
                      ? [file.uploadedFile]
                      : []
                  )
                );
              }}
              alterCaseFacts={alterCaseFacts}
              accepts={['image/*', '.pdf']}
              currentFiles={currentCaseFacts?.assessment_attachment ?? []}
            />
            <div className="mt-8 flex gap-8">
              <Button
                kind="primary"
                type="submit"
                loading={isSubmitting}
                disabled={Object.keys(errors).length > 0}
              >
                {t('general.updateAssessment')}
              </Button>
              <Button kind="outline_red" onClick={() => onClose()}>
                {t('general.cancel')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
